import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import "../styles/organizations.css";


const arcSteps = [
  {
    num: "01",
    tag: "Entry point · Team workshop",
    name: "Working Genius",
    desc: "Understand how your team naturally works — and where friction silently costs you.",
    connector: true,
  },
  {
    num: "02",
    tag: "Foundation · 6 weeks · Personal",
    name: "The Flow Journey",
    desc: "The inner work that makes organizational transformation possible. Leaders go first.",
    connector: true,
  },
  {
    num: "03",
    tag: "Systemic · Organizational",
    name: "Quantum Vision",
    desc: "An operating system for navigating the VUCA world — together, from flow.",
    connector: false,
  },
];

const wgItems = [
  "Individual Working Genius assessments for each team member",
  "A shared team map of natural strengths and frustrations",
  "Practical language for reducing friction and increasing collaboration",
  "Facilitated dialogue to surface hidden team dynamics",
  "Available in-person or virtually · Half-day or full-day",
];

const lfItems = [
  "Individual leaders complete the Flow Journey — moving from frozen identity to Flow Identity",
  "Those leaders bring presence, openness, and adaptive capacity into their teams",
  "The organization adopts Quantum Vision as a living cadence — not a plan",
  "Ripple effects extend outward — to clients, communities, and the broader world",
];

const qvCards = [
  {
    num: "01",
    tag: "Expand",
    name: "Perspective Expansion",
    desc: "Deliberately suspend single-track prediction. Explore bold possibilities before narrowing — unconstrained by immediate feasibility.",
    foundation: false,
  },
  {
    num: "02",
    tag: "Envision",
    name: "Multiple Plausible Futures",
    desc: "Craft distinct, coherent future scenarios — including disruptive ones — with observable signposts so you can recognize which path is emerging.",
    foundation: false,
  },
  {
    num: "03",
    tag: "Ground",
    name: "Persistent Foundations",
    desc: "Identify the values, strengths, and purposes that hold true across all futures. Balancing forces that keep you coherent while you adapt.",
    foundation: false,
  },
  {
    num: "04",
    tag: "Presence",
    name: "Reflection & Presencing",
    desc: "Regular, disciplined dialogues that surface hidden assumptions and emotional tone — keeping strategy alive rather than stagnant.",
    foundation: false,
  },
  {
    num: "05",
    tag: "Adapt",
    name: "Iterate, Learn & Adapt",
    desc: "Move in small cycles. Test lightly. Adjust without ego defensiveness. Fluid adaptation with integrity — not flawless execution.",
    foundation: false,
  },
  {
    num: "+",
    tag: "Foundation",
    name: "Flow Identity as Prerequisite",
    desc: "This operating system works best when leaders have done the inner work. The Flow Journey is the soil in which organizational flow takes root.",
    foundation: true,
  },
];

const lightCards = [
  {
    tag: "Speaking",
    name: "Keynote & Speaking",
    desc: "Alec and Stan speak on navigating the VUCA world, the future of leadership, and the relationship between inner presence and organizational resilience.",
    link: "mailto:alec@synergisticintelligence.com",
    linkLabel: "Inquire →",
    custom: false,
  },
  {
    tag: "Immersive",
    name: "Retreats",
    desc: "Multi-day off-site experiences combining presence practices, the Quantum Vision operating system, and shared inquiry. Designed to create shifts that last.",
    link: "mailto:alec@synergisticintelligence.com",
    linkLabel: "Inquire →",
    custom: false,
  },
  {
    tag: "Custom",
    name: "Not sure where to start?",
    desc: "Tell us what's happening in your organization. We'll suggest the right entry point — or be honest if we're not the right fit.",
    link: "mailto:alec@synergisticintelligence.com",
    linkLabel: "Start a conversation →",
    custom: true,
  },
];

const outcomes = [
  {
    num: "01",
    label: "Faster pivots",
    desc: "Less emotional volatility when the signal changes. More fluid adaptation with integrity.",
  },
  {
    num: "02",
    label: "Reduced defensiveness",
    desc: "Strategic dialogue becomes generative rather than territorial. Assumptions surface rather than fester.",
  },
  {
    num: "03",
    label: "Deeper alignment",
    desc: "Teams stop confusing activity with coherence. Trust builds. Communication sharpens.",
  },
  {
    num: "04",
    label: "Ripple effects outward",
    desc: "When leaders operate from flow, the effects extend beyond the team — to clients, communities, and the wider world.",
  },
];

// Shared fade-up variant
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export default function Organizations() {
  return (
    <>
      {/* ── HERO ── */}
      <section className="org-hero">
        <div className="org-hero-left">
          <p className="org-hero-eyebrow">For organizations &amp; teams</p>
          <h1>
            Transformation that begins within —<br />
            and <em>ripples outward.</em>
          </h1>
          <p className="org-hero-sub">
            The most resilient organizations we know are led by people who've done
            the inner work first. This page is for those leaders — and for those who
            sense that's where they need to start.
          </p>
          <div className="org-hero-actions">
            <Link to="/assessment" className="btn-light-org">
              Take the Flow Identity Assessment →
            </Link>
            <Link to="/flow-journey" className="btn-ghost-light-org">
              Explore the Flow Journey
            </Link>
          </div>
        </div>

        <div className="org-hero-quote">
          <p>
            "Disruption isn't something to manage or control. It's something to
            dance with — flowing with its rhythm rather than bracing against it."
          </p>
          <cite>From the Quantum Vision Allegory</cite>
        </div>
      </section>

      {/* ── ARC BAND ── */}
      <motion.section
        className="org-arc-band"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <div className="org-arc-band-inner">
          <p className="section-label">The natural progression</p>
          <h2 className="section-title" style={{ marginBottom: "0.5rem" }}>
            There's a natural order to this work.
          </h2>
          <p className="org-arc-intro">
            Each stage builds on the one before. Some leaders arrive at step one.
            Others arrive already at step two or three. Wherever you are — there's a
            clear next step.
          </p>
          <div className="org-arc-steps">
            {arcSteps.map(({ num, tag, name, desc, connector }) => (
              <div className="org-arc-step" key={num}>
                {connector && <div className="org-arc-connector">→</div>}
                <div className="org-arc-step-num">{num}</div>
                <div className="org-arc-step-tag">{tag}</div>
                <div className="org-arc-step-name">{name}</div>
                <div className="org-arc-step-desc">{desc}</div>
              </div>
            ))}
          </div>
          <p className="org-arc-hint">
            Not sure where you are? Start a conversation — we'll figure it out together.
          </p>
        </div>
      </motion.section>

      {/* ── WORKING GENIUS ── */}
      <motion.section
        className="org-wg"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        <div className="org-wg-left">
          <p className="section-label">Stage 01 · Entry point</p>
          <h2 className="section-title">
            Working Genius.<br />Where many journeys begin.
          </h2>
          <p className="org-wg-body">
            The Six Types of Working Genius is a powerful team diagnostic that
            clarifies how individuals contribute — and where friction naturally arises.
            In a half-day workshop, teams gain immediate, practical language for how
            they work best together.
          </p>
          <p className="org-wg-body">
            But what tends to surprise participants is what emerges underneath the
            practical insights:{" "}
            <strong>
              a clearer sense of the kind of work that makes them feel most alive.
            </strong>
          </p>
          <div className="org-wg-hint">
            "Many participants find it opens questions they didn't expect — about how
            they work, and who they are when they work best."
          </div>
          <p className="org-wg-body">That's a question worth following.</p>
          <div className="org-wg-actions">
            <a href="mailto:alec@synergisticintelligence.com" className="btn-primary-org">
              Book a Working Genius workshop →
            </a>
            <a href="mailto:alec@synergisticintelligence.com" className="btn-ghost-org">
              Learn more
            </a>
          </div>
        </div>

        <div className="org-wg-right">
          <div className="org-wg-what">
            <h4>What a workshop delivers</h4>
            {wgItems.map((item) => (
              <div className="org-wg-item" key={item}>
                <span className="org-wg-check">✓</span>
                <span>{item}</span>
              </div>
            ))}
            <p className="org-wg-lencioni">
              The Six Types of Working Genius was developed by Patrick Lencioni and
              The Table Group. Alec and Stan are certified facilitators who bring SI's
              awareness-based lens to the experience.
            </p>
          </div>
        </div>
      </motion.section>

      {/* ── LEADERS FIRST ── */}
      <motion.section
        className="org-leaders-first"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        <div>
          <p className="org-lf-label">Stage 02 · The inner work</p>
          <h2 className="org-lf-title">
            Organizations are<br />just people.<br />
            <em>Start there.</em>
          </h2>
          <p className="org-lf-body">
            The most powerful thing a leader can do for their organization is do this
            work themselves first. Not because it's a prerequisite we impose — but
            because we've seen, again and again, that the inner shift is what makes
            the outer change sustainable.
          </p>
          <p className="org-lf-body">
            The Flow Journey is a six-week, small-group experience that helps leaders
            move from fear-based survival patterns to a Flow Identity — the foundation
            from which everything else becomes possible.
          </p>
          <Link to="/flow-journey" className="org-lf-link">
            Explore the Flow Journey →
          </Link>
        </div>

        <div className="org-lf-right">
          <h4>The developmental arc</h4>
          {lfItems.map((item) => (
            <div className="org-lf-item" key={item}>
              <span className="org-lf-arrow">→</span>
              <span>{item}</span>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ── MID-PAGE ASSESSMENT CTA ── */}
      <div className="org-assessment-band">
        <p className="org-assessment-band-label">Not sure where your journey begins?</p>
        <p className="org-assessment-band-body">
          The Flow Identity Assessment takes 10 minutes and shows you exactly where
          you are — and what your next step looks like.
        </p>
        <div className="org-assessment-band-group">
          <Link to="/assessment" className="btn-primary-org">
            Take the Flow Identity Assessment →
          </Link>
          <span className="org-assessment-band-note">
            Free · 10 minutes · Personalized results
          </span>
        </div>
      </div>

      {/* ── QUANTUM VISION ── */}
      <motion.section
        className="org-qv"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        variants={fadeUp}
      >
        <div className="org-qv-inner">
          <div className="org-qv-header">
            <p className="section-label">Stage 03 · Organizational operating system</p>
            <h2 className="section-title">
              Quantum Vision.<br />A different way to navigate uncertainty.
            </h2>
            <p className="org-qv-intro">
              Not a strategic plan. Not a quarterly intervention. A living, rhythmic
              operating system for teams that have done the inner work — and are ready
              to build something different together.
            </p>
          </div>

          <div className="org-qv-grid">
            {qvCards.map(({ num, tag, name, desc, foundation }) => (
              <div
                className={`org-qv-card${foundation ? " foundation" : ""}`}
                key={num}
              >
                <div className="org-qv-num">{num}</div>
                <div className="org-qv-tag">{tag}</div>
                <div className="org-qv-name">{name}</div>
                <div className="org-qv-desc">{desc}</div>
              </div>
            ))}
          </div>

          <div className="org-qv-allegory">
            <div className="org-qv-allegory-text">
              <h4>Want to feel this before you understand it?</h4>
              <p>
                We wrote an allegory that brings Quantum Vision to life — not as a
                framework, but as a lived experience. Many leaders find it's the
                clearest explanation of why this work is different.
              </p>
            </div>
            <Link to="/allegory" className="org-qv-allegory-link">
              Read the allegory →
            </Link>
          </div>
        </div>
      </motion.section>

      {/* ── ORIGIN STORY ── */}
      <motion.section
        className="org-story"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <div className="org-story-inner">
          <p className="section-label">Where this came from</p>
          <h2 className="section-title">
            Born from real disruption.<br />Not a consulting room.
          </h2>
          <p className="org-story-body">
            Alec spent seventeen years inside financial services watching strategic
            plans become obsolete within months — and noticing the real friction was
            never the market. It was the humans on the teams: how they reacted to
            uncertainty, defended their assumptions, and clung to strategies that no
            longer fit.
          </p>
          <p className="org-story-body">
            Stan led a global electronics company generating over $120 million in
            annual revenue. Despite strong leadership and a disciplined strategic plan,
            the company eventually encountered forces no plan could solve. He had to
            personally lay off more than one hundred employees — many of whom were
            friends.
          </p>
          <div className="org-story-pull">
            <p>
              "What if the problem wasn't leadership effort or intelligence? What if
              the problem was how we were trying to navigate uncertainty itself?"
            </p>
          </div>
          <p className="org-story-body">
            That shared question is where this work began. They've built the allegory,
            the practitioner's guide, and the operating system. And they're still
            learning — openly — alongside the organizations they work with.
          </p>
        </div>
      </motion.section>

      {/* ── LIGHT OFFERINGS ── */}
      <motion.section
        className="org-light"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <div className="org-light-inner">
          <p className="section-label">Additional formats</p>
          <h2 className="section-title">Other ways we work together.</h2>
          <p className="org-light-intro">
            Depending on where your organization is, we also offer these as standalone
            engagements or as part of a deeper ongoing partnership.
          </p>
          <div className="org-light-grid">
            {lightCards.map(({ tag, name, desc, link, linkLabel, custom }) => (
              <div
                className={`org-light-card${custom ? " custom" : ""}`}
                key={name}
              >
                <div className="org-light-tag">{tag}</div>
                <h3 className="org-light-name">{name}</h3>
                <p className="org-light-desc">{desc}</p>
                <a href={link} className="org-light-link">{linkLabel}</a>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ── OUTCOMES ── */}
      <motion.section
        className="org-outcomes"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        <p className="section-label">What shifts</p>
        <h2 className="section-title">Observable outcomes.</h2>
        <div className="org-outcomes-grid">
          {outcomes.map(({ num, label, desc }) => (
            <div className="org-outcome-item" key={num}>
              <div className="org-outcome-num">{num}</div>
              <div className="org-outcome-text">
                <strong>{label}</strong>
                {desc}
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* ── FINAL CTA ── */}
      <section className="org-final-cta">
        <p className="org-final-cta-label">Begin</p>
        <h2>
          Not sure where you fit?<br />That's a good place to start.
        </h2>
        <p>
          Every engagement begins with a conversation. Tell us what's happening —
          and we'll figure out together whether, and how, we can help.
        </p>
        <div className="org-final-cta-group">
          <Link to="/assessment" className="btn-light-org">
            Take the Flow Identity Assessment →
          </Link>
          <a href="mailto:alec@synergisticintelligence.com" className="btn-ghost-light-org">
            Start a conversation
          </a>
        </div>
      </section>
    </>
  );
}
